import AudienceMergeMode from '@/Enums/AudienceTemplates/MergeMode.enum';
import type { IBaseAudienceTargeting } from '@/Interfaces/Audiences/Audience.interface';
import type { IAudienceControlOption } from '@/Interfaces/ControlOption.interface';

type Targeting = IBaseAudienceTargeting<IAudienceControlOption<number>>;

const dedupeByValue = (options: IAudienceControlOption<number>[]): IAudienceControlOption<number>[] => {
  const seen = new Set<number>();
  const result: IAudienceControlOption<number>[] = [];
  for (const option of options) {
    if (seen.has(option.value)) {
      continue;
    }
    seen.add(option.value);
    result.push(option);
  }
  return result;
};

/**
 * Merge a resolved template into the current audience targeting, preserving the
 * template's own AND-group structure (each inner array is an OR group; groups are
 * AND-ed together).
 *
 * AND appends every template group as new AND groups. OR widens the current
 * primary (first) OR group with the template's first group, keeps any existing
 * AND groups intact, then appends the template's remaining groups (so an internal
 * AND in the template is never collapsed). Excludes always union.
 */
export const mergeTemplateTargeting = (current: Targeting, addition: Targeting, mode: AudienceMergeMode): Targeting => {
  const currentInclude = current.include ?? [];
  const additionGroups = (addition.include ?? []).map(dedupeByValue).filter(group => group.length);
  const exclude = dedupeByValue([...(current.exclude ?? []), ...(addition.exclude ?? [])]);

  if (!additionGroups.length) {
    return { include: [...currentInclude], exclude };
  }

  if (mode === AudienceMergeMode.And || !currentInclude.length) {
    return { include: [...currentInclude, ...additionGroups], exclude };
  }

  // OR: widen the primary (first) OR group with the template's first group, keep
  // any existing AND groups intact, and append the template's remaining groups.
  const [firstGroup, ...restGroups] = additionGroups;
  const [currentFirst, ...currentRest] = currentInclude;
  const mergedFirst = dedupeByValue([...currentFirst, ...firstGroup]);
  return { include: [mergedFirst, ...currentRest, ...restGroups], exclude };
};
